import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from './auth/AuthContext';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#3AAF58', blue:'#FFFFFF',
 red:'#E05050', redBg:'rgba(224,80,80,.10)', redBorder:'rgba(224,80,80,.25)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.10)', amberBorder:'rgba(212,132,15,.25)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.10)', greenBorder:'rgba(58,175,88,.25)',
};

const DB_ROLES = [
 { id:1, name:'General Manager' },
 { id:2, name:'Front Desk' },
 { id:3, name:'Kitchen Chef' },
 { id:4, name:'Activity Manager' },
 { id:5, name:'Housekeeping' },
 { id:6, name:'Accounts' },
 { id:7, name:'Sales & CRM' },
 { id:8, name:'Reports & Analytics' },
 { id:9, name:'Sales Director' },
];

const inp = { width:'100%', padding:'10px 12px', background:'#22263A', border:'1px solid rgba(255,255,255,.14)', borderRadius:8, color:'#F0F2FF', fontSize:14, boxSizing:'border-box', outline:'none' };
const sel = { ...inp, cursor:'pointer' };

function Modal({ title, onClose, children, maxWidth=520 }) {
 return (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:'#1A1D27', border:'1px solid rgba(255,255,255,.14)', borderRadius:16, width:'100%', maxWidth, maxHeight:'90vh', overflow:'auto' }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 22px', borderBottom:'1px solid rgba(255,255,255,.08)' }}>
 <div style={{ fontWeight:700, fontSize:15, color:'#F0F2FF' }}>{title}</div>
 <button onClick={onClose} style={{ background:'none', border:'none', color:'#606880', fontSize:20, cursor:'pointer' }}></button>
 </div>
 <div style={{ padding:22 }}>{children}</div>
 </div>
 </div>
 );
}

export default function UserManagement() {
 const { isSuperAdmin, isAdmin, user: currentUser } = useAuth();
 const canManage = isSuperAdmin() || isAdmin();

 const [users, setUsers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState('');
 const [search, setSearch] = useState('');
 const [filterRole, setFilterRole] = useState('All');

 // Modals
 const [showAdd, setShowAdd] = useState(false);
 const [editUser, setEditUser] = useState(null);
 const [pwdUser, setPwdUser] = useState(null);
 const [confirmDel, setConfirmDel] = useState(null);

 // Forms
 const initForm = { name:'', email:'', phone:'', role_id:'7', status:'active' };
 const [form, setForm] = useState(initForm);
 const [newPwd, setNewPwd] = useState('');
 const [saving, setSaving] = useState(false);

 const set = (k,v) => setForm(p=>({...p,[k]:v}));

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const r = await fetch(`${API}/sessions/users`);
 const d = await r.json();
 setUsers(Array.isArray(d) ? d : []);
 } catch(e) { setError('Failed to load users'); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const showMsg = (msg, isError=false) => {
 if (isError) setError(msg); else setSuccess(msg);
 setTimeout(() => { setError(''); setSuccess(''); }, 3000);
 };

 // ── CREATE USER ────────────────────────────────────────────────────────────
 const handleCreate = async () => {
 if (!form.name.trim() || !form.email.trim()) { showMsg('Name and email are required', true); return; }
 setSaving(true);
 try {
 const res = await fetch(`${API}/sessions/users`, {
 method:'POST', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ ...form, password: form.password || 'Botanix@123' }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error||'Failed to create user');
 showMsg(`User ${form.name} created successfully!`);
 setShowAdd(false); setForm(initForm); load();
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 // ── EDIT USER ──────────────────────────────────────────────────────────────
 const handleEdit = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/sessions/users/${editUser.id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ name:form.name, email:form.email, phone:form.phone, role_id:form.role_id, status:form.status }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error||'Failed to update user');
 showMsg(`User ${form.name} updated!`);
 setEditUser(null); load();
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 // ── RESET PASSWORD ─────────────────────────────────────────────────────────
 const handleResetPwd = async () => {
 if (!newPwd.trim()) { showMsg('Enter new password', true); return; }
 setSaving(true);
 try {
 const res = await fetch(`${API}/sessions/users/${pwdUser.id}/password`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ password: newPwd }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error||'Failed to reset password');
 showMsg(`Password reset for ${pwdUser.name}!`);
 setPwdUser(null); setNewPwd('');
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 // ── TOGGLE STATUS ──────────────────────────────────────────────────────────
 const handleToggleStatus = async (u) => {
 const newStatus = u.status === 'active' ? 'inactive' : 'active';
 try {
 await fetch(`${API}/sessions/users/${u.id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ name:u.name, email:u.email, phone:u.phone, role_id:u.role_id||u.role, status:newStatus }),
 });
 showMsg(`${u.name} ${newStatus === 'active' ? 'enabled' : 'disabled'}!`);
 load();
 } catch(e) { showMsg('Failed to update status', true); }
 };

 // ── DELETE USER ────────────────────────────────────────────────────────────
 const handleDelete = async () => {
 try {
 await fetch(`${API}/sessions/users/${confirmDel.id}`, { method:'DELETE' });
 showMsg(`${confirmDel.name} deleted!`);
 setConfirmDel(null); load();
 } catch(e) { showMsg('Failed to delete user', true); }
 };

 const openEdit = (u) => {
 setForm({ name:u.name, email:u.email, phone:u.phone||'', role_id:u.role_id||'7', status:u.status||'active' });
 setEditUser(u);
 };

 // Filter
 let filtered = users;
 if (search) filtered = filtered.filter(u => [u.name,u.email,u.role].join(' ').toLowerCase().includes(search.toLowerCase()));
 if (filterRole!=='All') filtered = filtered.filter(u => u.role===filterRole);

 const roles = ['All',...new Set(users.map(u=>u.role).filter(Boolean))];

 const getAvatar = u => u.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
 const getStatusColor = status => status==='active' ? C.green : C.red;

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> User Management</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>{users.length} users · Manage access & permissions</div>
 </div>
 {canManage && (
 <button onClick={()=>{setForm(initForm);setShowAdd(true)}}
 style={{ padding:'10px 20px', background:C.accent, border:'none', borderRadius:10, color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}>
 + Add User
 </button>
 )}
 </div>

 {/* Messages */}
 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}> {error}</div>}
 {success && <div style={{ padding:'10px 14px', background:C.greenBg, border:`1px solid ${C.greenBorder||C.accent}`, borderRadius:8, color:C.green, fontSize:13, marginBottom:16 }}> {success}</div>}

 {/* Stats */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
 {[
 { label:'Total Users', value:users.length, color:C.text, },
 { label:'Active', value:users.filter(u=>u.status==='active').length, color:C.green, },
 { label:'Inactive', value:users.filter(u=>u.status!=='active').length, color:C.red, },
 { label:'Roles', value:new Set(users.map(u=>u.role)).size, color:C.blue, },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 16px' }}>
 <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
 <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Filters */}
 <div style={{ display:'flex', gap:10, marginBottom:16 }}>
 <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name or email..."
 style={{ ...inp, maxWidth:280, flex:1 }} />
 <select value={filterRole} onChange={e=>setFilterRole(e.target.value)} style={{ ...sel, width:200 }}>
 {roles.map(r=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={r}>{r}</option>)}
 </select>
 <button onClick={load} style={{ padding:'10px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}></button>
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading users…</div>}

 {/* User Cards */}
 {!loading && (
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
 {filtered.map(u => {
 const isCurrentUser = u.email === currentUser?.email;
 const isActive = u.status === 'active';
 return (
 <div key={u.id} style={{ background:C.surface, border:`1px solid ${isCurrentUser?C.accent:C.border}`, borderRadius:14, padding:18, opacity:isActive?1:.7 }}>
 <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:14 }}>
 <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg,#3AAF58,#2E8B47)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:800, color:'#fff', flexShrink:0 }}>
 {getAvatar(u)}
 </div>
 <div style={{ flex:1, minWidth:0 }}>
 <div style={{ display:'flex', alignItems:'center', gap:8 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text }}>{u.name}</div>
 {isCurrentUser && <span style={{ padding:'2px 8px', background:`${C.accent}20`, borderRadius:99, fontSize:10, fontWeight:700, color:C.accent }}>YOU</span>}
 </div>
 <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>{u.role}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:1 }}>#{u.id}</div>
 </div>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:isActive?C.greenBg:C.redBg, color:getStatusColor(u.status) }}>
 {isActive ? 'Active' : 'Inactive'}
 </span>
 </div>

 <div style={{ fontSize:12, color:C.text2, marginBottom:4 }}> {u.email}</div>
 {u.phone && <div style={{ fontSize:12, color:C.text2, marginBottom:4 }}> {u.phone}</div>}

 {canManage && (
 <div style={{ display:'flex', gap:6, marginTop:14, flexWrap:'wrap' }}>
 <button onClick={()=>openEdit(u)}
 style={{ padding:'6px 12px', background:'rgba(37,99,168,.2)', border:'1px solid rgba(37,99,168,.4)', borderRadius:7, color:'#60A0E0', cursor:'pointer', fontSize:12, fontWeight:600 }}>
 Edit
 </button>
 <button onClick={()=>{setPwdUser(u);setNewPwd('');}}
 style={{ padding:'6px 12px', background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:7, color:C.amber, cursor:'pointer', fontSize:12, fontWeight:600 }}>
 Password
 </button>
 {!isCurrentUser && (
 <button onClick={()=>handleToggleStatus(u)}
 style={{ padding:'6px 12px', background:isActive?C.redBg:C.greenBg, border:`1px solid ${isActive?C.redBorder:C.greenBorder||C.accent}`, borderRadius:7, color:isActive?C.red:C.green, cursor:'pointer', fontSize:12, fontWeight:600 }}>
 {isActive ? ' Disable' : ' Enable'}
 </button>
 )}
 {!isCurrentUser && isSuperAdmin() && (
 <button onClick={()=>setConfirmDel(u)}
 style={{ padding:'6px 12px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:7, color:C.red, cursor:'pointer', fontSize:12, fontWeight:600 }}>
 Delete
 </button>
 )}
 </div>
 )}
 </div>
 );
 })}
 {filtered.length===0 && <div style={{ gridColumn:'span 3', textAlign:'center', padding:40, color:C.text2 }}>No users found</div>}
 </div>
 )}

 {/* ── ADD USER MODAL ── */}
 {showAdd && (
 <Modal title="Add New User" onClose={()=>setShowAdd(false)}>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
 <div style={{ gridColumn:'span 2' }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Full Name *</label>
 <input style={inp} type="text" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Full name" />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Email *</label>
 <input style={inp} type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="email@botanix.in" />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Phone</label>
 <input style={inp} type="text" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="Mobile number" />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Role</label>
 <select style={sel} value={form.role_id} onChange={e=>set('role_id',e.target.value)}>
 {DB_ROLES.map(r=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={r.id} value={r.id}>{r.name}</option>)}
 </select>
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Initial Password</label>
 <input style={inp} type="text" value={form.password||''} onChange={e=>set('password',e.target.value)} placeholder="Default: Botanix@123" />
 </div>
 <div style={{ gridColumn:'span 2' }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Status</label>
 <select style={sel} value={form.status} onChange={e=>set('status',e.target.value)}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="active">Active</option>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="inactive">Inactive</option>
 </select>
 </div>
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
 <button onClick={()=>setShowAdd(false)} style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleCreate} disabled={saving} style={{ padding:'10px 22px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:saving?.6:1 }}>
 {saving?'Creating…':'Create User'}
 </button>
 </div>
 </Modal>
 )}

 {/* ── EDIT USER MODAL ── */}
 {editUser && (
 <Modal title={`Edit — ${editUser.name}`} onClose={()=>setEditUser(null)}>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
 <div style={{ gridColumn:'span 2' }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Full Name *</label>
 <input style={inp} type="text" value={form.name} onChange={e=>set('name',e.target.value)} />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Email *</label>
 <input style={inp} type="email" value={form.email} onChange={e=>set('email',e.target.value)} />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Phone</label>
 <input style={inp} type="text" value={form.phone} onChange={e=>set('phone',e.target.value)} />
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Role / Department</label>
 <select style={sel} value={form.role_id} onChange={e=>set('role_id',e.target.value)}>
 {DB_ROLES.map(r=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={r.id} value={r.id}>{r.name}</option>)}
 </select>
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Status</label>
 <select style={sel} value={form.status} onChange={e=>set('status',e.target.value)}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="active">Active</option>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="inactive">Inactive</option>
 </select>
 </div>
 </div>
 <div style={{ marginTop:14, padding:'10px 14px', background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:8, fontSize:12, color:C.amber }}>
 To change password, use the Password button on the user card.
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
 <button onClick={()=>setEditUser(null)} style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleEdit} disabled={saving} style={{ padding:'10px 22px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:saving?.6:1 }}>
 {saving?'Saving…':'Save Changes'}
 </button>
 </div>
 </Modal>
 )}

 {/* ── PASSWORD MODAL ── */}
 {pwdUser && (
 <Modal title={`Reset Password — ${pwdUser.name}`} onClose={()=>setPwdUser(null)}>
 <div style={{ marginBottom:14, padding:'10px 14px', background:C.surface2, borderRadius:8, fontSize:13, color:C.text2 }}>
 Resetting password for: <strong style={{color:C.text}}>{pwdUser.name}</strong> ({pwdUser.email})
 </div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>New Password *</label>
 <input style={inp} type="text" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="Enter new password" />
 <div style={{ fontSize:11, color:C.text3, marginTop:6 }}>
 Tip: Use format like Name@123 for easy remembering
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
 <button onClick={()=>setPwdUser(null)} style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleResetPwd} disabled={saving||!newPwd.trim()} style={{ padding:'10px 22px', background:C.amber, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:(saving||!newPwd.trim())?.6:1 }}>
 {saving?'Resetting…':' Reset Password'}
 </button>
 </div>
 </Modal>
 )}

 {/* ── DELETE CONFIRM ── */}
 {confirmDel && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.redBorder}`, borderRadius:16, padding:28, maxWidth:400, width:'100%' }}>
 <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:8 }}>Delete {confirmDel.name}?</div>
 <div style={{ fontSize:13, color:C.text2, marginBottom:20 }}>This will permanently remove this user. This action cannot be undone.</div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
 <button onClick={()=>setConfirmDel(null)} style={{ padding:'9px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleDelete} style={{ padding:'9px 22px', background:C.red, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700 }}>Delete</button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
